import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useClickOutside } from "@/hooks/useClickOutside";
import type { CheckboxDropdownOption } from "./SearchableCheckboxDropdown";
import {
  EMAIL_MODAL_DROPDOWN_LIST_CLASS,
  EMAIL_MODAL_DROPDOWN_OPTION_ACTIVE_CLASS,
  EMAIL_MODAL_DROPDOWN_OPTION_CLASS,
  EMAIL_MODAL_DROPDOWN_PANEL_CLASS,
  EMAIL_MODAL_DROPDOWN_SEARCH_INPUT_CLASS,
  EMAIL_MODAL_DROPDOWN_SEARCH_ROW_CLASS,
  EMAIL_MODAL_FIELD_TRIGGER_CLASS,
  EMAIL_MODAL_FIELD_TRIGGER_OPEN_CLASS,
  EMAIL_MODAL_LABEL_CLASS,
} from "./styles";

interface SearchableSelectDropdownProps {
  label: string;
  placeholder: string;
  options: CheckboxDropdownOption[];
  value: string;
  onChange: (value: string) => void;
  leadingIcon?: React.ReactNode;
}

export const SearchableSelectDropdown: React.FC<
  SearchableSelectDropdownProps
> = ({ label, placeholder, options, value, onChange, leadingIcon }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useClickOutside<HTMLDivElement>(
    () => setOpen(false),
    open,
  );

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options;

    return options.filter((option) =>
      option.label.toLowerCase().includes(query),
    );
  }, [options, search]);

  const displayLabel = useMemo(() => {
    if (!value) return placeholder;
    return (
      options.find((option) => option.value === value)?.label ?? placeholder
    );
  }, [options, placeholder, value]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setOpen(false);
    setSearch("");
  };

  const handleToggle = () => {
    setOpen((prev) => {
      if (prev) setSearch("");
      return !prev;
    });
  };

  return (
    <div ref={containerRef} className="flex flex-col gap-1.5">
      <Label className={EMAIL_MODAL_LABEL_CLASS}>{label}</Label>

      <div className="relative flex flex-col">
        <button
          type="button"
          aria-expanded={open}
          onClick={handleToggle}
          className={cn(
            EMAIL_MODAL_FIELD_TRIGGER_CLASS,
            open && EMAIL_MODAL_FIELD_TRIGGER_OPEN_CLASS,
          )}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2 truncate">
            {leadingIcon}
            <span
              className={cn(
                "truncate font-normal",
                !value ? "text-black/45" : "text-[#0A0A0A]",
              )}
            >
              {displayLabel}
            </span>
          </span>
          <ChevronDown
            className={cn(
              "size-4 shrink-0 text-black/45 transition-transform duration-200",
              open && "rotate-180",
            )}
          />
        </button>

        {open ? (
          <div className={EMAIL_MODAL_DROPDOWN_PANEL_CLASS}>
            <div className={EMAIL_MODAL_DROPDOWN_SEARCH_ROW_CLASS}>
              <Search className="size-4 shrink-0 text-black/45" aria-hidden />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("analytics.common.search", "Search")}
                className={EMAIL_MODAL_DROPDOWN_SEARCH_INPUT_CLASS}
              />
            </div>

            <div className={EMAIL_MODAL_DROPDOWN_LIST_CLASS}>
              {filteredOptions.length === 0 ? (
                <p className="px-3 py-2.5 text-sm text-black/45">
                  {t("analytics.common.noResultsFound", "No results found")}
                </p>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = value === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleSelect(option.value)}
                      className={cn(
                        EMAIL_MODAL_DROPDOWN_OPTION_CLASS,
                        isSelected && EMAIL_MODAL_DROPDOWN_OPTION_ACTIVE_CLASS,
                      )}
                    >
                      <span className="truncate">{option.label}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
